import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, Loader2, CheckCircle, AlertTriangle, BadgeCheck, FileText } from 'lucide-react';
import { supabase } from '../../src/lib/supabase';
import { useToast } from '../../src/contexts/ToastContext';
import { useAuth } from '../../src/contexts/AuthContext';
import { logAction, logUsageEvent } from '../../src/lib/logger';
import { cancelarBoletosSicoob } from '../../src/lib/cancelarBoletoSicoob';
import { propsFundoModal } from '../../src/lib/backdropModal';

interface QuitarFaturasModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    preSelectedFatura?: any;
}

interface FaturaPendente {
    id: string;
    uc_codigo: string;
    cliente_nome: string;
    mes_referencia: string;
    valor_boleto_vigor: number;
    vencimento_boleto: string | null;
    status_boleto_vigor: string;
    status_fatura: string;
    num_cobranca_boleto?: string | null;
}

const normalizeUcLocal = (uc: any): string => {
    if (typeof uc !== 'string' && typeof uc !== 'number') return '';
    return String(uc).replace(/[.\-\s]/g, '').replace(/^0+/, '');
};

const formatDateBR = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-';
    const [ano, mes, dia] = dateStr.split('T')[0].split('-');
    return `${dia}/${mes}/${ano}`;
};

const QuitarFaturasModal: React.FC<QuitarFaturasModalProps> = ({ isOpen, onClose, onSuccess, preSelectedFatura }) => {
    const { user, profile } = useAuth();
    const { addToast } = useToast();
    const [loading, setLoading] = useState(false);

    const [faturaSearchTerm, setFaturaSearchTerm] = useState('');
    const [faturasPendentes, setFaturasPendentes] = useState<FaturaPendente[]>([]);
    const [selectedFaturaIds, setSelectedFaturaIds] = useState<Set<string>>(new Set());
    const [selectedFaturasData, setSelectedFaturasData] = useState<Map<string, FaturaPendente>>(new Map());
    const [loadingFaturas, setLoadingFaturas] = useState(false);
    const [observacao, setObservacao] = useState('');

    useEffect(() => {
        if (!isOpen) return;
        setLoading(false);
        setFaturasPendentes([]);
        setSelectedFaturaIds(new Set());
        setSelectedFaturasData(new Map());

        if (preSelectedFatura) {
            const seed: FaturaPendente = {
                id: String(preSelectedFatura.id),
                uc_codigo: preSelectedFatura.uc_codigo || '',
                cliente_nome: preSelectedFatura.cliente_nome || '',
                mes_referencia: preSelectedFatura.mes_referencia || '',
                valor_boleto_vigor: Number(preSelectedFatura.valor_boleto_vigor) || 0,
                vencimento_boleto: preSelectedFatura.vencimento_boleto || null,
                status_boleto_vigor: preSelectedFatura.status_boleto_vigor || '',
                status_fatura: preSelectedFatura.status_fatura || '',
                num_cobranca_boleto: preSelectedFatura.num_cobranca_boleto || null,
            };
            setSelectedFaturaIds(new Set([seed.id]));
            setSelectedFaturasData(new Map([[seed.id, seed]]));
            setFaturaSearchTerm(preSelectedFatura.cliente_nome || preSelectedFatura.uc_codigo || '');
        } else {
            setFaturaSearchTerm('');
        }
    }, [isOpen]);

    // Default da observação a partir das faturas selecionadas
    useEffect(() => {
        if (selectedFaturasData.size === 0) return;
        const selecionadas = Array.from(selectedFaturasData.values());
        const meses = selecionadas.map(f => f.mes_referencia).join(', ');
        setObservacao(prev => prev || `Pago via cobrança avulsa/unificada — meses ${meses}`);
    }, [selectedFaturasData]);

    // Busca faturas direto por nome/código (normalizado)
    useEffect(() => {
        const termo = faturaSearchTerm.trim();
        if (termo.length < 3) {
            setFaturasPendentes([]);
            return;
        }
        const fetchFaturas = async () => {
            setLoadingFaturas(true);
            try {
                const termoUc = normalizeUcLocal(termo);
                const orFilter = termoUc
                    ? `cliente_nome.ilike.%${termo}%,uc_codigo.ilike.%${termoUc}%`
                    : `cliente_nome.ilike.%${termo}%`;
                const { data, error } = await supabase
                    .from('faturas')
                    .select('id, uc_codigo, cliente_nome, mes_referencia, valor_boleto_vigor, vencimento_boleto, status_boleto_vigor, status_fatura, num_cobranca_boleto')
                    .or(orFilter)
                    .in('status_boleto_vigor', ['Em aberto', 'Vencido'])
                    // Sem valor não há o que quitar — ver NotificacaoMoraModal.
                    .gt('valor_boleto_vigor', 0)
                    .in('status_fatura', ['Com boleto calculado', 'Com boleto emitido / A enviar', 'Cobrança enviada'])
                    .order('vencimento_boleto', { ascending: true })
                    .limit(50);

                if (error) throw error;
                setFaturasPendentes((data || []) as FaturaPendente[]);
            } catch (err) {
                console.error('Erro ao buscar faturas:', err);
            } finally {
                setLoadingFaturas(false);
            }
        };
        const t = setTimeout(fetchFaturas, 400);
        return () => clearTimeout(t);
    }, [faturaSearchTerm]);

    const toggleFatura = (fatura: FaturaPendente) => {
        const id = String(fatura.id);
        setSelectedFaturaIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) { next.delete(id); } else { next.add(id); }
            return next;
        });
        setSelectedFaturasData(prev => {
            const next = new Map(prev);
            if (next.has(id)) { next.delete(id); } else { next.set(id, fatura); }
            return next;
        });
    };

    const totalSelecionadas = Array.from(selectedFaturasData.values());
    const faturasComBoleto = totalSelecionadas.filter(f => !!f.num_cobranca_boleto);
    const totalValor = totalSelecionadas.reduce((s, f) => s + (Number(f.valor_boleto_vigor) || 0), 0);

    const handleSave = async () => {
        if (totalSelecionadas.length === 0) {
            addToast('Selecione ao menos uma fatura para quitar.', 'error');
            return;
        }
        setLoading(true);
        try {
            // 1. Cancelar boletos emitidos no Sicoob para faturas selecionadas.
            // A divergência "fatura quitada / boleto vivo" fica gravada na própria
            // fatura pelo helper — o toast abaixo é só o aviso imediato.
            const { falhas, rotulosFalha: falhasCancelamento } = await cancelarBoletosSicoob(
                faturasComBoleto,
                'Quitado via cobrança avulsa/unificada'
            );

            // 2. Marcar faturas como Quitado (por último, protegido pelo trigger)
            const nomeQuem = profile?.full_name || user?.email || 'Sistema';
            const { error } = await supabase
                .from('faturas')
                .update({
                    status_boleto_vigor: 'Quitado',
                    quitado_em: new Date().toISOString(),
                    quitado_por_nome: nomeQuem,
                    quitacao_observacao: observacao.trim() || 'Pago via cobrança avulsa/unificada'
                })
                .in('id', totalSelecionadas.map(f => f.id));
            if (error) throw error;

            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'faturas', acao: 'quitar_faturas', origem: 'acao' });
            await logAction(user?.id || 'SYSTEM', 'QUITAR_FATURAS', {
                faturas: totalSelecionadas.map(f => f.id),
                total: totalValor,
                observacao: observacao.trim(),
                falhas_cancelamento_sicoob: falhasCancelamento,
                // httpCode + resposta por boleto: sem isso não há conciliação depois
                // (Sicoob — Guia Operacional §10.9).
                detalhe_cancelamento_sicoob: falhas
            });

            if (falhasCancelamento.length > 0) {
                addToast(
                    `⚠️ ${falhasCancelamento.length} boleto(s) (${falhasCancelamento.join(', ')}) NÃO puderam ser cancelados no Sicoob. Cancele manualmente para evitar pagamento duplicado.`,
                    'warning'
                );
            }
            addToast(`${totalSelecionadas.length} fatura(s) marcada(s) como Quitado!`, 'success');
            if (onSuccess) onSuccess();
            onClose();
        } catch (error: any) {
            console.error(error);
            addToast(`Erro ao quitar faturas: ${error.message || error}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" style={{ zIndex: 9999 }} {...propsFundoModal(() => onClose())}>
            <div className="bg-white dark:bg-gray-800 w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-fade-in" onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className="px-6 py-5 bg-gradient-to-r from-teal-500/10 to-transparent border-b border-gray-100 dark:border-gray-700 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-teal-500/10 rounded-xl text-teal-600 dark:text-teal-400">
                            <BadgeCheck size={22} />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white">Quitar Faturas (pago via avulsa)</h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                Dê baixa em boletos já pagos numa cobrança avulsa/unificada
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors">
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                    <div>
                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-2">
                            <Search size={16} className="text-teal-500" />
                            Buscar faturas Em Aberto / Vencidas
                        </label>
                        <div className="relative">
                            <input
                                type="text"
                                placeholder="Buscar por nome do cliente ou código da UC..."
                                value={faturaSearchTerm}
                                onChange={(e) => setFaturaSearchTerm(e.target.value)}
                                className="w-full px-4 py-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all pl-11"
                            />
                            <Search className="absolute left-4 top-3.5 text-gray-400" size={18} />
                            {loadingFaturas && <Loader2 size={16} className="absolute right-4 top-3.5 animate-spin text-teal-500" />}
                        </div>
                        <p className="text-[11px] text-gray-400 mt-1">
                            Funciona para condomínios e UCs sem cadastro (busca direto nas faturas).
                        </p>
                    </div>

                    {faturaSearchTerm.trim().length >= 3 && !loadingFaturas && faturasPendentes.length === 0 && (
                        <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-700 text-sm text-gray-500 text-center">
                            Nenhuma fatura em aberto ou vencida encontrada para "{faturaSearchTerm}".
                        </div>
                    )}

                    {(faturasPendentes.length > 0 || selectedFaturasData.size > 0) && (
                        <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden divide-y divide-gray-100 dark:divide-gray-700/50 max-h-72 overflow-y-auto">
                            {faturasPendentes.map(fatura => {
                                const isSelected = selectedFaturaIds.has(String(fatura.id));
                                const isVencido = (fatura.status_boleto_vigor || '').toLowerCase() === 'vencido';
                                return (
                                    <label
                                        key={fatura.id}
                                        className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${isSelected ? 'bg-teal-50 dark:bg-teal-900/20' : 'bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800/50'}`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => toggleFatura(fatura)}
                                            className="w-4 h-4 accent-teal-600 rounded"
                                        />
                                        <div className="flex-1 flex items-center justify-between gap-2 min-w-0">
                                            <div className="min-w-0">
                                                <span className="text-sm font-bold text-gray-900 dark:text-white">{fatura.mes_referencia}</span>
                                                <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">Venc. {formatDateBR(fatura.vencimento_boleto)}</span>
                                                <p className="text-[11px] text-gray-400 truncate">UC {fatura.uc_codigo?.trim()} • {fatura.cliente_nome}</p>
                                            </div>
                                            <div className="flex items-center gap-2 shrink-0">
                                                <span className="text-sm font-bold text-gray-900 dark:text-white">
                                                    R$ {Number(fatura.valor_boleto_vigor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                                </span>
                                                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${isVencido ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'}`}>
                                                    {fatura.status_boleto_vigor}
                                                </span>
                                                {fatura.num_cobranca_boleto && (
                                                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                                                        Boleto emitido
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </label>
                                );
                            })}

                            {/* Selecionadas fora do resultado atual */}
                            {Array.from(selectedFaturasData.values())
                                .filter(f => !faturasPendentes.some(fp => String(fp.id) === String(f.id)))
                                .map(fatura => {
                                    const isVencido = (fatura.status_boleto_vigor || '').toLowerCase() === 'vencido';
                                    return (
                                        <label
                                            key={`sel-${fatura.id}`}
                                            className="flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors bg-teal-50 dark:bg-teal-900/20"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={true}
                                                onChange={() => toggleFatura(fatura)}
                                                className="w-4 h-4 accent-teal-600 rounded"
                                            />
                                            <div className="flex-1 flex items-center justify-between gap-2 min-w-0">
                                                <div className="min-w-0">
                                                    <span className="text-sm font-bold text-gray-900 dark:text-white">{fatura.mes_referencia}</span>
                                                    <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">Venc. {formatDateBR(fatura.vencimento_boleto)}</span>
                                                    <p className="text-[11px] text-gray-400 truncate">UC {fatura.uc_codigo?.trim()} • {fatura.cliente_nome}</p>
                                                </div>
                                                <div className="flex items-center gap-2 shrink-0">
                                                    <span className="text-sm font-bold text-gray-900 dark:text-white">
                                                        R$ {Number(fatura.valor_boleto_vigor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                                    </span>
                                                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${isVencido ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'}`}>
                                                        {fatura.status_boleto_vigor}
                                                    </span>
                                                </div>
                                            </div>
                                        </label>
                                    );
                                })}

                            {selectedFaturasData.size > 0 && (
                                <div className="px-4 py-2.5 bg-teal-50 dark:bg-teal-900/20 flex items-center justify-between sticky bottom-0">
                                    <span className="text-xs font-semibold text-teal-700 dark:text-teal-300">
                                        {selectedFaturasData.size} fatura(s) selecionada(s)
                                    </span>
                                    <span className="text-sm font-bold text-teal-700 dark:text-teal-300">
                                        Total: R$ {totalValor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                    </span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Observação */}
                    {selectedFaturasData.size > 0 && (
                        <div>
                            <label className="text-xs font-bold text-gray-400 uppercase flex items-center gap-1.5 mb-1.5">
                                <FileText size={12} className="text-teal-500" /> Observação da quitação
                            </label>
                            <textarea
                                value={observacao}
                                onChange={(e) => setObservacao(e.target.value)}
                                rows={2}
                                placeholder="Ex: Pago via cobrança avulsa — meses JAN, FEV, MAR"
                                className="w-full px-4 py-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all resize-none text-sm"
                            />
                        </div>
                    )}

                    {/* Aviso sobre boletos que serão cancelados */}
                    {faturasComBoleto.length > 0 && (
                        <div className="flex items-start gap-2 px-3 py-2.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
                            <AlertTriangle size={15} className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                            <p className="text-xs text-amber-700 dark:text-amber-300 font-medium">
                                {faturasComBoleto.length} fatura(s) possuem boleto emitido no Sicoob. Ao finalizar, esses boletos serão cancelados automaticamente.
                            </p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-5 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex items-center justify-between">
                    <button onClick={onClose} className="px-4 py-2.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-750 font-semibold rounded-xl transition-colors">
                        Cancelar
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={loading || totalSelecionadas.length === 0}
                        className="px-6 py-2.5 bg-teal-600 text-white font-bold rounded-xl shadow-lg shadow-teal-500/30 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center gap-2 disabled:opacity-50 disabled:hover:scale-100"
                    >
                        {loading ? (
                            <><Loader2 className="animate-spin" size={18} /> Quitando...</>
                        ) : (
                            <><CheckCircle size={18} /> Quitar {totalSelecionadas.length > 0 ? `(${totalSelecionadas.length})` : ''}</>
                        )}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default QuitarFaturasModal;
